// NewAdjustmentModal — draft a governed stock correction (draft → approval →
// apply): reason code + comment mandatory, CD/over-threshold escalates to
// Pharmacist/IM approval. Extracted verbatim from pages/Adjustments.tsx so the
// global quick-actions launcher can reuse it; the Adjustments page renders it
// unchanged. Self-contained: fetches its own products/batches/bins and already
// handles the All-Stores case (store derives from the picked batch).
import { useEffect, useState } from 'react';
import { Icon } from '@/foundation/icons';
import { Button, Field, Input, Modal, Select, Textarea } from '@/foundation/primitives';
import { useStore } from '@/store/store';
import { inStockProductOptions } from '@/domain/skuOptions';
import { fmtDate, TODAY } from '@/domain/dates';
import { ADJ_REASONS } from '@/domain/reference';
import { warnBox } from './components';
import { useBatches, useBins, useCreateAdjustment, useProducts } from './api';

export const ADJ_THRESHOLD = 10;

const blankAdj = { product: '', batch: '', delta: '', reason: '', comment: '', evidence: false };

export function NewAdjustmentModal({
  open,
  onClose,
  initialProduct,
}: {
  open: boolean;
  onClose: () => void;
  /** Pre-select a product (its snapshot_key / sku) so the user can adjust it directly — e.g.
   *  launched by clicking a product row instead of searching the picker. */
  initialProduct?: string;
}) {
  const { store: activeStore, toast, me, role } = useStore();
  const { data: products = [] } = useProducts();
  const { data: batches = [] } = useBatches();
  const { data: bins = [] } = useBins();
  const createAdjustment = useCreateAdjustment();
  const [f, setF] = useState(blankAdj);
  useEffect(() => {
    if (!open) return;
    // Resolve the requested product to an actual in-stock picker value: exact match first, then a
    // bare-code match (the launching row's key may carry a different level/version prefix). Falls
    // back to the raw value so the modal still opens even if nothing matches.
    const opts = inStockProductOptions(batches, activeStore);
    const bare = (s: string) => s.split(':').pop();
    const resolved = initialProduct
      ? (opts.find((o) => o.value === initialProduct)?.value ??
        opts.find((o) => bare(o.value) === bare(initialProduct))?.value ??
        initialProduct)
      : '';
    setF({ ...blankAdj, product: resolved });
    // Only re-seed when the modal opens or the requested product changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialProduct]);
  // The product picker only offers what the store HOLDS in stock (options keyed by the batch's
  // snapshot_key), so `f.product` IS the batch key — every selectable product has batches, and
  // there's never a "product picked but no batch" dead end. Adjustments can correct ANY batch
  // with stock (incl. expired/held — that's how you write them off), so we filter on on-hand qty.
  const product = products.find((p) => p.snapshotKey === f.product || p.sku === f.product);
  const prodBatches = batches.filter(
    (b) =>
      b.product === f.product &&
      (activeStore === 'ALL' || b.store === activeStore) &&
      (b.qty ?? 0) > 0,
  );
  // with All Stores active, the adjustment's store comes from the picked batch
  const store =
    activeStore === 'ALL'
      ? (prodBatches.find((b) => b.id === f.batch)?.store ?? 'ST-MAN')
      : activeStore;
  const delta = Number(f.delta) || 0;
  const needsHigh = (product && product.cd) || Math.abs(delta) > ADJ_THRESHOLD;
  // A POSITIVE adjustment adds units into the batch's bin — warn (soft limit, never block) if that
  // would push the bin near/over its configured capacity, matching the Locations page & Add-stock.
  const selectedBatch = prodBatches.find((b) => b.id === f.batch);
  const targetBin = selectedBatch ? bins.find((bn) => bn.code === selectedBatch.bin) : undefined;
  const capNotice =
    targetBin && targetBin.cap && delta > 0
      ? (() => {
          const projected = targetBin.occupiedQty + delta;
          const pct = Math.round((projected / targetBin.cap) * 100);
          return pct >= 90
            ? { code: targetBin.code, projected, cap: targetBin.cap, pct, over: pct >= 100 }
            : null;
        })()
      : null;
  const submit = () => {
    createAdjustment.mutate({
      product: f.product,
      batch: f.batch,
      store,
      delta,
      reason: f.reason,
      comment: f.comment,
      status: 'Pending Approval',
      by: me,
      byRole: role,
      approver: null,
      date: fmtDate(TODAY),
      cd: !!(product && product.cd),
    });
    toast('Adjustment drafted — sent for approval', 'success');
    onClose();
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New Adjustment"
      subtitle="Draft a stock correction for approval."
      width={500}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Field label="Product" required>
          <Select
            value={f.product}
            onChange={(e) => setF({ ...f, product: e.target.value, batch: '' })}
            placeholder="Select a product in stock…"
            options={inStockProductOptions(batches, activeStore)}
          />
        </Field>
        <div style={{ display: 'flex', gap: 14 }}>
          <Field label="Batch" required>
            <Select
              value={f.batch}
              onChange={(e) => setF({ ...f, batch: e.target.value })}
              placeholder={f.product ? 'Select batch…' : 'Pick product first'}
              options={prodBatches.map((b) => ({
                value: b.id,
                label: `${b.batchNo ?? b.id} · ${b.qty} on hand`,
              }))}
              disabled={!f.product}
            />
          </Field>
          <Field label="Quantity Δ (±)" required hint="Negative reduces stock">
            <Input
              value={f.delta}
              onChange={(e) => setF({ ...f, delta: e.target.value })}
              type="number"
              placeholder="-4"
            />
          </Field>
        </div>
        <Field label="Reason code" required>
          <Select
            value={f.reason}
            onChange={(e) => setF({ ...f, reason: e.target.value })}
            placeholder="Select reason…"
            options={ADJ_REASONS}
          />
        </Field>
        <Field label="Comment" required>
          <Textarea
            value={f.comment}
            onChange={(e) => setF({ ...f, comment: e.target.value })}
            placeholder="Provide detailed explanation for the adjustment…"
          />
        </Field>
        <div
          onClick={() => setF({ ...f, evidence: !f.evidence })}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px',
            borderRadius: 'var(--radius-md)',
            border: `1.5px dashed ${f.evidence ? 'var(--primary-main)' : 'var(--grey-300)'}`,
            cursor: 'pointer',
            background: f.evidence ? 'var(--primary-lighter)' : 'var(--grey-100)',
          }}
        >
          <Icon
            name={f.evidence ? 'filecheck' : 'upload'}
            size={20}
            color={f.evidence ? 'var(--primary-main)' : 'var(--text-secondary)'}
          />
          <span
            style={{
              fontSize: 'var(--fs-body)',
              fontWeight: 600,
              color: f.evidence ? 'var(--primary-dark)' : 'var(--text-secondary)',
            }}
          >
            {f.evidence ? 'evidence-photo.jpg attached' : 'Attach evidence (photo, count sheet)…'}
          </span>
        </div>
        {needsHigh && (
          <div
            style={{ ...warnBox, background: 'var(--warning-soft)', color: 'var(--warning-dark)' }}
          >
            <Icon name="shield" size={18} color="var(--warning-dark)" />
            <span>
              {product && product.cd ? 'Controlled drug' : `Delta exceeds ±${ADJ_THRESHOLD}`} —
              requires Pharmacist / Inventory Manager approval
              {product && product.cd ? ' with dual sign-off' : ''}.
            </span>
          </div>
        )}
        {capNotice && (
          <div
            style={{
              ...warnBox,
              background: capNotice.over ? 'var(--error-soft)' : 'var(--warning-soft)',
              color: capNotice.over ? 'var(--error-dark)' : 'var(--warning-dark)',
            }}
          >
            <Icon
              name="alerts"
              size={18}
              color={capNotice.over ? 'var(--error-dark)' : 'var(--warning-dark)'}
            />
            <span>
              {capNotice.over ? 'Over capacity' : 'Near full'}: adding +{delta} to bin{' '}
              <strong>{capNotice.code}</strong> → {capNotice.projected}/{capNotice.cap} (
              {capNotice.pct}%).
              {capNotice.over ? ' This bin will exceed its configured capacity.' : ''}
            </span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            icon="arrowright"
            iconRight="arrowright"
            onClick={submit}
            disabled={!f.product || !f.batch || !f.delta || !f.reason || !f.comment}
          >
            Send for Approval
          </Button>
        </div>
      </div>
    </Modal>
  );
}
